import { useEffect, useState } from "react";
import { api, formatPrice } from "@/lib/api";
import AdminNav, { AdminHeader } from "@/components/AdminNav";
import { AlertTriangle, DollarSign, Package, Truck, TrendingUp, Activity } from "lucide-react";

function Stat({ label, value, hint, icon: Icon, color = "#FF5C00" }) {
  return (
    <div className="border border-zinc-800 bg-[#0a0a0c] p-4 rounded-sm">
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-zinc-500">
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color }} />}{label}
      </div>
      <div className="font-mono text-2xl font-bold text-white mt-2">{value}</div>
      {hint && <div className="font-mono text-[10px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/dashboard").then(({ data }) => setData(data)),
      api.get("/admin/automation-summary").then(({ data }) => setAutomation(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <div className="container-tight py-8" data-testid="admin-dashboard-page">
      <AdminNav />
      <AdminHeader title="DASHBOARD" subtitle="Business KPIs & supplier performance snapshot" />
      {loading || !data ? (
        <div className="font-mono text-zinc-500">LOADING...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="TOTAL ORDERS" value={data.orders.total} icon={Package} />
            <Stat label="PENDING PAYMENT" value={data.orders.pending} icon={Activity} color="#eab308" />
            <Stat label="HUMAN REVIEW" value={data.orders.exceptions} icon={AlertTriangle} color="#ef4444" />
            <Stat label="FORWARDED" value={data.orders.forwarded} icon={Truck} color="#3b82f6" />
          </div>
          {automation && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="automation-summary">
              <Stat label="AUTO ORDERS TODAY" value={automation.automatic_orders_today}
                    icon={Activity} color="#22c55e" />
              <Stat label="AWAITING ACCEPT" value={automation.awaiting_supplier_acceptance}
                    icon={Truck} color="#3b82f6" />
              <Stat label="AWAITING TRACKING" value={automation.awaiting_tracking}
                    icon={Truck} color="#8b5cf6" />
              <Stat label="SUPPLIER FAILURES" value={automation.supplier_failures_total}
                    icon={AlertTriangle} color="#ef4444" />
              <Stat label="SYNC ERRORS" value={automation.supplier_sync_errors}
                    icon={AlertTriangle} color="#eab308" />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
            <Stat label="GROSS REVENUE" value={formatPrice(data.revenue.gross)} icon={DollarSign} />
            <Stat label="EST. CONTRIBUTION PROFIT" value={formatPrice(data.revenue.estimated_contribution_profit)}
                  hint={`${data.revenue.estimated_margin_percent}% margin`} icon={TrendingUp} color="#22c55e" />
            <Stat label="FLAGGED SUPPLIER OFFERS" value={data.products.flagged ?? 0}
                  hint={`${data.products.out_of_stock}/${data.products.total} out-of-stock`} icon={AlertTriangle}
                  color="#eab308" />
          </div>

          <div className="border border-zinc-800 bg-[#0a0a0c] rounded-sm">
            <div className="px-4 py-3 border-b border-zinc-800 font-mono text-xs tracking-widest uppercase text-zinc-300 flex items-center gap-2">
              <Truck className="w-4 h-4 text-[#FF5C00]" /> Supplier Performance
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase border-b border-zinc-900">
                  <th className="p-3 text-left">Code</th>
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-right">Orders Sent</th>
                  <th className="p-3 text-right">Fulfilled</th>
                  <th className="p-3 text-right">Errors</th>
                  <th className="p-3 text-right">Reliability</th>
                </tr>
              </thead>
              <tbody>
                {data.suppliers.map((s) => {
                  const p = s.performance || {};
                  return (
                    <tr key={s.id} className="border-b border-zinc-900 hover:bg-black/40" data-testid={`dash-supplier-${s.code}`}>
                      <td className="p-3 font-mono text-xs">{s.code}</td>
                      <td className="p-3">{s.name}</td>
                      <td className="p-3"><StatusBadge status={s.status} /></td>
                      <td className="p-3 font-mono text-right">{p.orders_sent || 0}</td>
                      <td className="p-3 font-mono text-right">{p.fulfilled || 0}</td>
                      <td className="p-3 font-mono text-right">{p.errors || 0}</td>
                      <td className="p-3 font-mono text-right">{((p.reliability_score ?? 0.5) * 100).toFixed(0)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const style = {
    live: "bg-green-500/10 text-green-400 border-green-500/30",
    test: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    not_connected: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    disabled: "bg-red-500/10 text-red-400 border-red-500/30",
  }[status] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/30";
  return <span className={`font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border rounded-sm ${style}`}>{status}</span>;
}
